import { LiveList, LiveMap, LiveObject, Room } from "@liveblocks/client";
import { useRoom } from "@liveblocks/react";
import { useAtom } from "jotai";
import { useEffect, useState } from "react";
import { currentSpaceAtom } from "../jotai/atoms";
import { AnyBlock } from "../types";

export function useMigration() {
  const room = useRoom();
  const [completed, setCompleted] = useState<Record<string, boolean>>({});
  const [currentSpace] = useAtom(currentSpaceAtom);

  useEffect(() => {
    if (!currentSpace || completed[currentSpace.id]) return;

    (async () => {
      const { root } = await room.getStorage<Storage>();
      const history = root.get("migrationHistory") || {};

      for (const migration of MIGRATIONS) {
        if (history[migration.name]) continue;

        await migration.run(room);
        history[migration.name] = true;
        root.set("migrationHistory", history);
      }

      setCompleted((x) => ({ ...x, [currentSpace.id]: true }));
    })();
  }, [room, currentSpace, completed]);

  return currentSpace && completed[currentSpace.id];
}

/* ------------- migrations -------------- */

const MIGRATIONS: { name: string; run: MigrationFunc }[] = [
  { name: "removeBlockIds", run: removeBlockIds },
];

type MigrationFunc = ((room: Room) => Promise<void>) & { name: string };

type MigrationHistory = {
  [name: string]: true;
};

type Storage = {
  blockIds?: LiveList<string>;
  blocks?: LiveMap<string, LiveObject<AnyBlock>>;
  migrationHistory?: MigrationHistory;
};

async function removeBlockIds(room: Room) {
  const { root } = await room.getStorage<Storage>();
  const blockIds = root.get("blockIds");
  const blocks = root.get("blocks");
  if (!blockIds || !blocks) return;

  const active = new Set(blockIds);

  room.batch(() => {
    for (const [id, block] of blocks.entries()) {
      if (!active.has(id)) {
        block.set("deleted", true);
      }
    }
  });
}
